// Oike — where a request came from (Solicitudes). Shared by the public form's
// function (netlify/functions/request.mts), intake-review.mts and the screens.
// Pure: no network.
//
// Three different things, never mixed:
// - captured: what the link carried when the form was opened (utm_* and the
//   identified link code). It says which link was used, not who shared it: a
//   forwarded link keeps the original identification.
// - declared: what the person answered in "¿Cómo llegaste a mí?".
// - manual: what Ale registers by hand (referral, direct message, own prospecting).
// Nothing here counts visits or clicks: an origin exists only when a request
// is sent. Parameters never carry personal data.

// The public form (public/home.html, served by Netlify as /home.html) and its section
export const FORM_PATH = '/home.html';
export const FORM_ANCHOR = '#form';

// The only parameters the form keeps; anything else in the URL is ignored.
// oike_form and oike_topic say what the form is for (src/requestForms.js), not where it came from.
export const ORIGIN_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'oike_link', 'oike_form', 'oike_topic'];
const PARAM_RE = /^[a-z0-9][a-z0-9._-]{0,59}$/;

/** A parameter value as stored: lowercase slug, short; never an email, a phone number or free text. */
export function cleanParam(value) {
  if (typeof value !== 'string') return null;
  const v = value.trim().toLowerCase();
  if (!PARAM_RE.test(v)) return null;          // spaces, @, +, accents, too long…
  if ((v.match(/\d/g) || []).length >= 7) return null; // looks like a phone or an id number
  return v;
}

/** The allowed parameters of a URL query (URLSearchParams, a plain object or a query string), cleaned. */
export function pickOriginParams(source) {
  const get = typeof source === 'string' ? ((p) => new URLSearchParams(source).get(p))
    : source && typeof source.get === 'function' ? ((p) => source.get(p))
    : ((p) => (source && typeof source === 'object' ? source[p] : null));
  const out = {};
  for (const p of ORIGIN_PARAMS) { const v = cleanParam(get(p)); if (v) out[p] = v; }
  return out;
}

export const slug = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);

// First version: links for TikTok and LinkedIn, plus any other channel by hand
export const LINK_PRESETS = [
  { key: 'tiktok-bio', label: 'Bio de TikTok', channel: 'TikTok', utm_source: 'tiktok', utm_medium: 'social', utm_campaign: 'bio' },
  { key: 'linkedin-perfil', label: 'Perfil de LinkedIn', channel: 'LinkedIn', utm_source: 'linkedin', utm_medium: 'social', utm_campaign: 'perfil' },
  { key: 'linkedin-campana', label: 'Publicación o campaña de LinkedIn', channel: 'LinkedIn', utm_source: 'linkedin', utm_medium: 'social', utm_campaign: '', askCampaign: true },
  { key: 'other', label: 'Otro canal', channel: '', utm_source: '', utm_medium: '', utm_campaign: '', askChannel: true, askCampaign: true },
];

const CHANNEL_NAMES = { tiktok: 'TikTok', linkedin: 'LinkedIn', instagram: 'Instagram', youtube: 'YouTube', facebook: 'Facebook', newsletter: 'Newsletter', email: 'Email', whatsapp: 'WhatsApp' };
export const channelName = (source) => CHANNEL_NAMES[source] || (source ? source.charAt(0).toUpperCase() + source.slice(1) : '');

/** The parameters of a new identified link, validated; { error } when something is missing. */
export function linkParams({ preset, channel, campaign, content, formType = 'team', topic = '' }) {
  const p = LINK_PRESETS.find(x => x.key === preset);
  if (!p) return { error: 'Elegí un canal.' };
  const utm_source = p.askChannel ? cleanParam(slug(channel)) : p.utm_source;
  if (!utm_source) return { error: 'Escribí el canal (por ejemplo: instagram).' };
  const utm_campaign = p.askCampaign ? cleanParam(slug(campaign)) : p.utm_campaign;
  if (p.askCampaign && !p.askChannel && !utm_campaign) return { error: 'Escribí el nombre de la campaña.' };
  const utm_content = content ? cleanParam(slug(content)) : null;
  const utm_medium = p.askChannel ? 'referral' : p.utm_medium;
  // What the form is for: team (the usual form, not written in the URL), individual or webinar, and a topic code
  const oike_form = formType === 'individual' || formType === 'webinar' ? formType : '';
  const oike_topic = topic ? cleanParam(topic) || '' : '';
  const code = [utm_source, utm_campaign, oike_form, oike_topic, utm_content].filter(Boolean).join('-').slice(0, 60);
  const label = p.askChannel ? `${channelName(utm_source)}${utm_campaign ? ` · ${utm_campaign}` : ''}`
    : `${p.label}${p.askCampaign && utm_campaign ? ` · ${utm_campaign}` : ''}`;
  // The name says what the link is for when it is not the usual team form, so two links of one channel never look alike
  const kind = { individual: 'Individual', webinar: 'Webinar' }[oike_form] || '';
  return { utm_source, utm_medium, utm_campaign: utm_campaign || '', utm_content: utm_content || '', oike_form, oike_topic, code, channel: p.channel || channelName(utm_source), label: `${label}${kind ? ` · ${kind}` : ''}${utm_content ? ` · ${utm_content}` : ''}` };
}

/** The URL of an identified link: the current form with its UTM parameters and the link code. */
export function linkUrl(origin, { utm_source, utm_medium, utm_campaign, utm_content, oike_form, oike_topic, code }) {
  const q = new URLSearchParams();
  if (utm_source) q.set('utm_source', utm_source);
  if (utm_medium) q.set('utm_medium', utm_medium);
  if (utm_campaign) q.set('utm_campaign', utm_campaign);
  if (utm_content) q.set('utm_content', utm_content);
  if (code) q.set('oike_link', code);
  if (oike_form) q.set('oike_form', oike_form);
  if (oike_topic) q.set('oike_topic', oike_topic);
  return `${String(origin || '').replace(/\/+$/, '')}${FORM_PATH}?${q.toString()}${FORM_ANCHOR}`;
}

// ── Airtable fields (see docs/solicitudes/origen.md) ──
// Table "Source" (tblciUlYmvQHJm71w). Its "Stakeholders" field is text, not a link: it is never written here
export const LINK_FIELDS = { name: 'Source Name', code: 'Link Code', channel: 'Channel', utmSource: 'UTM Source', utmMedium: 'UTM Medium', utmCampaign: 'UTM Campaign', utmContent: 'UTM Content', destination: 'Destination' };
export const REQUEST_FIELDS = { method: 'Origin Method', link: 'Origin Link', channel: 'Origin Channel', campaign: 'Origin Campaign', params: 'Origin Params', capturedAt: 'Origin Captured At', declared: 'Declared Source', manual: 'Manual Origin', manualNote: 'Manual Origin Note', manualAt: 'Manual Origin At' };
export const PERSON_FIELDS = { request: 'First Origin Request', method: 'First Origin Method', channel: 'First Origin Channel', campaign: 'First Origin Campaign', at: 'First Origin At', note: 'First Origin Note' };
export const OPPORTUNITY_FIELDS = { request: 'Origin Request' };
// The three links allow several records in Airtable; Oike always writes one and reads the first.

/** The people a link brought: those whose first origin is a request sent with it. Counted from data, never stored on the link. */
export function newPeopleOfLink(linkId, intakes = [], stakeholders = []) {
  const viaLink = new Set(intakes.filter(r => [].concat(r.fields?.[REQUEST_FIELDS.link] || []).includes(linkId)).map(r => r.id));
  return stakeholders.filter(s => viaLink.has([].concat(s.fields?.[PERSON_FIELDS.request] || [])[0]));
}

export const METHOD_LABELS = {
  link: 'Enlace identificado',
  utm: 'Parámetros de campaña (enlace no registrado en Oike)',
  manual: 'Registro manual',
  direct: 'Sin identificar',
};
export const MANUAL_ORIGINS = { referral: 'Referido', direct_message: 'Mensaje directo', own_prospecting: 'Prospección propia', other: 'Otro' };
export const UNKNOWN = 'Sin identificar';
export const DECLARED_MAX = 300;
export const NOTE_MAX = 500;

const DAY = 864e5;
/** When the link was opened, as the form reports it: kept only if it is a real time within the last 30 days. */
export function capturedAtOf(value, now = new Date()) {
  const t = typeof value === 'string' ? Date.parse(value) : NaN;
  if (!Number.isFinite(t) || t > now.getTime() + 5 * 60e3 || t < now.getTime() - 30 * DAY) return now.toISOString();
  return new Date(t).toISOString();
}

/**
 * The origin fields of a new request. `link` is the identified link found by
 * its code (an Airtable record of Sources) or null. Without parameters the
 * request is "Sin identificar" (method direct), never assigned to a channel.
 */
export function requestOriginFields({ params = {}, capturedAt, declared, link = null, now = new Date() }) {
  const p = pickOriginParams(params);
  const f = {};
  if (link) {
    const lf = link.fields || {};
    f[REQUEST_FIELDS.method] = 'link';
    f[REQUEST_FIELDS.link] = [link.id];
    f[REQUEST_FIELDS.channel] = lf[LINK_FIELDS.channel] || channelName(p.utm_source);
    f[REQUEST_FIELDS.campaign] = lf[LINK_FIELDS.utmCampaign] || p.utm_campaign || '';
  } else if (p.utm_source || p.utm_campaign) {
    f[REQUEST_FIELDS.method] = 'utm';
    f[REQUEST_FIELDS.channel] = channelName(p.utm_source);
    f[REQUEST_FIELDS.campaign] = p.utm_campaign || '';
  } else {
    f[REQUEST_FIELDS.method] = 'direct';
  }
  if (Object.keys(p).length) f[REQUEST_FIELDS.params] = JSON.stringify(p);
  f[REQUEST_FIELDS.capturedAt] = f[REQUEST_FIELDS.method] === 'direct' ? now.toISOString() : capturedAtOf(capturedAt, now);
  const d = typeof declared === 'string' ? declared.replace(/\s+/g, ' ').trim().slice(0, DECLARED_MAX) : '';
  if (d) f[REQUEST_FIELDS.declared] = d;
  for (const k of Object.keys(f)) if (f[k] === '') delete f[k];
  return f;
}

/** The person's first origin, written once when the person is created from this request. */
export function firstOriginFields(requestId, originFields) {
  const f = {
    [PERSON_FIELDS.request]: [requestId],
    [PERSON_FIELDS.method]: originFields[REQUEST_FIELDS.method] || 'direct',
    [PERSON_FIELDS.at]: originFields[REQUEST_FIELDS.capturedAt],
  };
  if (originFields[REQUEST_FIELDS.channel]) f[PERSON_FIELDS.channel] = originFields[REQUEST_FIELDS.channel];
  if (originFields[REQUEST_FIELDS.campaign]) f[PERSON_FIELDS.campaign] = originFields[REQUEST_FIELDS.campaign];
  return f;
}

/** A new opportunity created from a request: it keeps the request as its origin (no copy of the origin). */
export function opportunityFieldsFromRequest(request, today = new Date().toISOString().slice(0, 10)) {
  const f = request?.fields || {};
  const company = String(val(f, 'Company Context') || '').split('—')[0].trim() || 'Solicitud';
  const out = { 'Deal/Opp name': `${company} · solicitud`, 'Stage': 'Discovery', 'Opening date': today, [OPPORTUNITY_FIELDS.request]: [request.id] };
  const accountId = [].concat(f['Account'] || [])[0];
  if (accountId) out['Account'] = [accountId];
  return out;
}

/** A manual origin, validated: { kind, note } → fields, or throws. `prefix` picks request or person fields. */
export function manualOriginFields({ kind, note }, target, now = new Date()) {
  if (!MANUAL_ORIGINS[kind]) throw new Error('invalid manual origin');
  const n = typeof note === 'string' ? note.trim().slice(0, NOTE_MAX) : '';
  if (target === 'request') return { [REQUEST_FIELDS.manual]: kind, [REQUEST_FIELDS.manualNote]: n, [REQUEST_FIELDS.manualAt]: now.toISOString() };
  return { [PERSON_FIELDS.method]: 'manual', [PERSON_FIELDS.channel]: MANUAL_ORIGINS[kind], [PERSON_FIELDS.note]: n, [PERSON_FIELDS.at]: now.toISOString() };
}

const val = (fields, k) => { const v = fields?.[k]; return v && typeof v === 'object' && !Array.isArray(v) ? (v.name ?? v.value ?? '') : (v ?? ''); };
const parseJson = (s) => { try { const o = JSON.parse(s); return o && typeof o === 'object' ? o : {}; } catch { return {}; } };

/** A request's origin, read from its fields: captured, declared and manual kept apart. */
export function requestOriginOf(fields = {}) {
  const method = val(fields, REQUEST_FIELDS.method) || '';
  const manual = val(fields, REQUEST_FIELDS.manual) || '';
  return {
    known: method === 'link' || method === 'utm' || !!manual,
    captured: method === 'link' || method === 'utm' ? {
      method, linkId: [].concat(fields[REQUEST_FIELDS.link] || [])[0] || null,
      channel: val(fields, REQUEST_FIELDS.channel), campaign: val(fields, REQUEST_FIELDS.campaign),
      params: parseJson(val(fields, REQUEST_FIELDS.params)), at: val(fields, REQUEST_FIELDS.capturedAt) || null,
    } : null,
    recorded: !!method, // false: sent before origins were recorded
    declared: val(fields, REQUEST_FIELDS.declared) || '',
    manual: manual ? { kind: manual, label: MANUAL_ORIGINS[manual] || manual, note: val(fields, REQUEST_FIELDS.manualNote), at: val(fields, REQUEST_FIELDS.manualAt) || null } : null,
  };
}

/** One line for lists: the captured origin, else the manual one, else "Sin identificar". The declared answer is never used here. */
export function requestOriginLabel(fields) {
  const o = requestOriginOf(fields);
  if (o.captured) return [o.captured.channel || 'Enlace', o.captured.campaign].filter(Boolean).join(' · ');
  if (o.manual) return o.manual.label;
  return UNKNOWN;
}

/** A person's first origin (Stakeholders), or { known: false } → "Sin identificar". */
export function firstOriginOf(fields = {}) {
  const method = val(fields, PERSON_FIELDS.method) || '';
  const requestId = [].concat(fields[PERSON_FIELDS.request] || [])[0] || null;
  const known = method === 'link' || method === 'utm' || method === 'manual';
  return {
    known, method, requestId,
    label: known ? [val(fields, PERSON_FIELDS.channel), val(fields, PERSON_FIELDS.campaign)].filter(Boolean).join(' · ') || METHOD_LABELS[method] : UNKNOWN,
    methodLabel: METHOD_LABELS[method] || UNKNOWN,
    note: val(fields, PERSON_FIELDS.note) || '',
    at: val(fields, PERSON_FIELDS.at) || null,
    // A first origin that is still unknown can be completed by hand; a known one is never overwritten
    canRecord: !known,
  };
}
